import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";

const API = "/api";

// Inline conversion block for blog articles. Renders REAL catalog bouquets
// (image + name + price + Order CTA) for an explicit list of product ids,
// preserving the given order. Embedded in article markdown via a
// [[PRODUCTS:id1,id2,...]] marker handled in BlogArticlePage.
export const InlineProductCards = ({ ids = [], city, cityData, cityName, lang }) => {
  const [byId, setById] = useState({});

  useEffect(() => {
    if (!city) return;
    let active = true;
    axios
      .get(`${API}/products?city=${city}`)
      .then((res) => {
        if (!active) return;
        const map = {};
        (res.data || []).forEach((p) => { map[p.id] = p; if (p.name) map[p.name] = p; });
        setById(map);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [city]);

  const products = ids.map((id) => byId[id]).filter(Boolean);
  if (!products.length) return null;

  const sym = cityData?.currencySymbol || "$";
  const cityHref = `/${city}${lang !== "ru" ? `?lang=${lang}` : ""}`;
  const orderLabel = lang === "en" ? "Order" : lang === "tr" ? "Siparis" : "Заказать";
  const allLabel =
    lang === "en" ? "View all bouquets" : lang === "tr" ? "Tum buketleri gor" : "Смотреть все букеты";

  const altFor = (name) =>
    lang === "en"
      ? `${name} — birthday flower delivery in ${cityName}`
      : lang === "tr"
      ? `${name} — ${cityName} dogum gunu cicek teslimati`
      : `${name} — доставка цветов на день рождения ${cityData?.namePrepositional || cityName}`;

  return (
    <div className="not-prose my-8" data-testid="inline-product-cards">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        {products.map((p) => {
          const name =
            lang === "en" && p.name_en ? p.name_en : lang === "tr" && p.name_tr ? p.name_tr : p.name;
          return (
            <Link
              to={cityHref}
              key={p.id}
              className="group flex flex-col rounded-2xl overflow-hidden border border-gray-100 hover:border-pink-200 hover:shadow-md transition-all bg-white"
              data-testid={`inline-product-${p.id}`}
            >
              <div className="aspect-square overflow-hidden bg-gray-50">
                <img
                  src={p.image}
                  alt={altFor(name)}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-3 flex flex-col flex-1">
                <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 min-h-[2.5rem] m-0">
                  {name}
                </h3>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-base font-bold text-gray-900">
                    {sym}{p.price}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-pink-600 group-hover:gap-1.5 transition-all">
                    {orderLabel}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      <Link
        to={cityHref}
        className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-pink-600 hover:text-pink-700 transition-colors no-underline"
        data-testid="inline-products-view-all"
      >
        {allLabel}
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
};
